'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useBiometrics } from '@/hooks/useBiometrics';
import { useCorporate } from '@/context/CorporateContext';
import { useWeb3 } from '@/context/Web3Context';
import { useRouter, usePathname } from 'next/navigation';

const INACTIVITY_TIMEOUT_MS = 300000; // 5 minutes
const STORAGE_KEY = 'waas_last_activity';
const THROTTLE_MS = 2500; // 2.5 seconds throttle

export function InactivityLock({ children }: { children: React.ReactNode }) {
    const [isLocked, setIsLocked] = useState(false);
    const { verifyUser, isEnabled } = useBiometrics();
    const { config } = useCorporate();
    const { logout } = useWeb3();
    const router = useRouter();
    const pathname = usePathname();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastUpdateRef = useRef<number>(0);

    const isPublicPath = pathname === '/' ||
        pathname === '/login' ||
        pathname?.startsWith('/cnv-transparencia') ||
        pathname?.startsWith('/cnv-riesgos') ||
        pathname?.startsWith('/wallet/welcome');

    const checkInactivity = useCallback(() => {
        if (!isEnabled || isLocked || isPublicPath) return;
        const lastActivityStr = localStorage.getItem(STORAGE_KEY);
        if (lastActivityStr) {
            const lastActivity = parseInt(lastActivityStr, 10);
            if (Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
                console.log("[Security] Persistent session locked due to inactivity.");
                setIsLocked(true);
            }
        }
    }, [isEnabled, isLocked]);

    const updateActivity = useCallback(() => {
        if (isLocked || !isEnabled) return;
        
        const now = Date.now();
        // Throttle localStorage writes to prevent performance degradation
        if (now - lastUpdateRef.current > THROTTLE_MS) {
            localStorage.setItem(STORAGE_KEY, now.toString());
            lastUpdateRef.current = now;
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            console.log("[Security] User inactive for 5 minutes. Locking session.");
            setIsLocked(true);
        }, INACTIVITY_TIMEOUT_MS);
    }, [isLocked, isEnabled]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Check immediately on mount for persistent locks (F5 bypass prevention)
        checkInactivity();
        updateActivity();

        const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
        const handleActivity = () => updateActivity();

        events.forEach(evt => window.addEventListener(evt, handleActivity));

        // Setup interval to periodically check inactivity across tabs
        const interval = setInterval(checkInactivity, 5000);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            clearInterval(interval);
            events.forEach(evt => window.removeEventListener(evt, handleActivity));
        };
    }, [checkInactivity, updateActivity]);

    const handleUnlock = async () => {
        const success = await verifyUser();
        if (success) {
            setIsLocked(false);
            const now = Date.now();
            localStorage.setItem(STORAGE_KEY, now.toString());
            lastUpdateRef.current = now;
            updateActivity();
        }
    };

    const handleLogoutEscape = async () => {
        try {
            console.log("[Security] User initiated secure logout from lock screen.");
            localStorage.removeItem(STORAGE_KEY);
            setIsLocked(false);
            await logout();
            router.replace('/login');
        } catch (err) {
            console.error("Error logging out", err);
        }
    };

    return (
        <>
            {children}
            {isLocked && !isPublicPath && (
                <div 
                    className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#000000]/80 backdrop-blur-2xl"
                >
                    <div className="flex flex-col items-center justify-center p-8 bg-[#0a111b]/80 border border-white/5 rounded-[2rem] shadow-2xl max-w-[320px] mx-auto w-full text-center" style={{ borderColor: 'color-mix(in srgb, var(--tenant-primary) 20%, transparent)' }}>
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--tenant-primary) 15%, transparent)' }}>
                             <span className="material-symbols-outlined font-variation-fill" style={{ fontSize: '32px', color: 'var(--tenant-primary, #3b82f6)' }}>lock</span>
                        </div>
                        <h2 className="text-2xl font-black text-white mb-2 tracking-tight">Sesión Bloqueada</h2>
                        <p className="text-slate-400 text-[13px] font-medium mb-8 leading-relaxed px-2">
                            Por seguridad, tu sesión ha sido bloqueada tras {INACTIVITY_TIMEOUT_MS / 60000} minutos de inactividad.
                        </p>
                        
                        <div className="flex flex-col gap-3 w-full">
                            <button 
                                onClick={handleUnlock}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-white transition-all hover:opacity-90 active:scale-95 shadow-lg"
                                style={{ backgroundColor: 'var(--tenant-primary, #3b82f6)', boxShadow: '0 8px 25px -5px color-mix(in srgb, var(--tenant-primary) 40%, transparent)' }}
                            >
                                <span className="material-symbols-outlined text-[20px]">fingerprint</span>
                                Desbloquear
                            </button>
                            
                            <button 
                                onClick={handleLogoutEscape}
                                className="w-full flex items-center justify-center gap-2 py-3 mt-1 rounded-full font-bold text-slate-400 border border-white/5 bg-white/5 transition-all hover:bg-white/10 hover:text-white active:scale-95"
                            >
                                <span className="material-symbols-outlined text-[18px]">logout</span>
                                Cerrar Sesión
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
